import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { AuthShell, CodeInput, PasswordInput } from "../components/AuthShell";
import { Icon } from "../components/icons";
import { Alert, Button, Field, Input } from "../components/ui";
import { ApiError } from "../lib/api";
import { api } from "../lib/api";

type Step = "email" | "code" | "success";
const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleRequestCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/password-reset/request", {
        method: "POST",
        body: { email },
        anonymous: true,
      });
      setStep("code");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not send reset code. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/password-reset/confirm", {
        method: "POST",
        body: {
          email,
          code,
          newPassword,
          ...(requires2FA ? { totpCode } : {}),
        },
        anonymous: true,
      });
      setStep("success");
    } catch (cause) {
      if (cause instanceof ApiError) {
        if (cause.message.includes("Two-factor authentication code required")) {
          setRequires2FA(true);
          setError("Two-factor authentication code required");
        } else {
          setError(cause.message);
        }
      } else {
        setError("Could not reset password. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (step === "success") {
    return (
      <AuthShell
        title="Password reset successful"
        subtitle="Your password has been reset. You can now sign in with your new password."
        announce
        footer={
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            to="/login"
            state={location.state}
          >
            Sign in
          </Link>
        }
      >
        <div className="space-y-4 text-center">
          <span className="mx-auto grid size-10 place-items-center rounded-md border border-mint/25 bg-mint/8 text-mint">
            <Icon name="check" className="size-4" />
          </span>
          <Button
            onClick={() => navigate("/login", { state: location.state })}
            variant="inverse"
            size="lg"
            className="w-full"
          >
            Continue to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (step === "code") {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle="Enter the reset code and choose a new password. Codes expire after 15 minutes."
        footer={
          <button
            type="button"
            className="text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
            onClick={() => {
              setStep("email");
              setCode("");
              setNewPassword("");
              setTotpCode("");
              setRequires2FA(false);
              setUseRecoveryCode(false);
              setError(null);
            }}
          >
            Use a different email
          </button>
        }
      >
        <form onSubmit={handleResetPassword} className="space-y-5">
          {error && <Alert>{error}</Alert>}

          <div className="border-b border-line pb-4 text-center">
            <p className="micro text-accent">Reset request</p>
            <p className="mt-2 break-all text-sm font-medium text-fg">{email}</p>
            <p className="mt-1 text-xs leading-5 text-fg-muted">
              If an account exists for this address, a reset code will arrive.
            </p>
          </div>

          <CodeInput id="code" label="Reset code" value={code} onChange={setCode} autoFocus />

          <Field label="New password" htmlFor="newPassword" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              minLength={MIN_PASSWORD_LENGTH}
            />
          </Field>

          {requires2FA && (
            <div className="space-y-3 border-t border-line pt-4">
              <div>
                <p className="text-sm font-medium text-fg">Two-factor authentication</p>
                <p className="mt-0.5 text-xs leading-5 text-fg-muted">
                  This account also needs an authenticator or recovery code.
                </p>
              </div>
              <CodeInput
                id="totpCode"
                label={useRecoveryCode ? "Recovery code" : "Authentication code"}
                value={totpCode}
                onChange={setTotpCode}
                numeric={!useRecoveryCode}
              />
              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode((current) => !current);
                  setTotpCode("");
                }}
                className="text-left text-sm text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              >
                {useRecoveryCode ? "Use authenticator code" : "Use recovery code"}
              </button>
            </div>
          )}

          <Button type="submit" variant="inverse" size="lg" loading={submitting} className="w-full">
            Reset password
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email address. If an account exists, a reset code will arrive shortly."
      footer={
        <>
          Remember your password?{" "}
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            to="/login"
            state={location.state}
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleRequestCode} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            size="xl"
            surface="canvas"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="inverse" size="lg" loading={submitting} className="w-full">
          Send reset code
        </Button>
      </form>
    </AuthShell>
  );
}
